import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Trash2, Sparkles, Check, Database } from "lucide-react";

export default function QuestionBank() {
  const [items, setItems] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filters, setFilters] = useState({ search: "", qtype: "all", difficulty: "all", subject: "all" });

  const load = async () => {
    const params = {
      search: filters.search,
      subject: filters.subject === "all" ? "" : filters.subject,
      qtype: filters.qtype === "all" ? "" : filters.qtype,
      difficulty: filters.difficulty === "all" ? "" : filters.difficulty,
    };
    const { data } = await api.get("/question-bank", { params });
    setItems(data);
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filters]);
  useEffect(() => { api.get("/question-bank/subjects").then((r) => setSubjects(r.data)).catch(() => {}); }, [items.length]);

  const remove = async (id) => { await api.delete(`/question-bank/${id}`); load(); };

  return (
    <DashboardLayout nav={adminNav} title="Question Bank">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Question Bank</h1>
        <p className="text-muted-foreground mt-1">{items.length} reusable questions across all subjects.</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search question text…" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="bank-search" />
        </div>
        <Select value={filters.subject} onValueChange={(v) => setFilters({ ...filters, subject: v })}>
          <SelectTrigger className="w-48" data-testid="bank-subject"><SelectValue placeholder="Subject" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All subjects</SelectItem>
            {subjects.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.qtype} onValueChange={(v) => setFilters({ ...filters, qtype: v })}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>{["all", "mcq", "msq", "short", "long", "descriptive"].map((t) => <SelectItem key={t} value={t} className="uppercase">{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.difficulty} onValueChange={(v) => setFilters({ ...filters, difficulty: v })}>
          <SelectTrigger className="w-32"><SelectValue placeholder="Difficulty" /></SelectTrigger>
          <SelectContent>{["all", "Easy", "Medium", "Hard"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center">
          <Database className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">No questions in the bank yet. Generate questions inside an examination with "save to bank" enabled.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((q, i) => (
            <div key={q.id} className="border border-border bg-card p-4" data-testid={`bank-q-${q.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2 text-xs">
                  <span className="px-1.5 py-0.5 bg-accent text-accent-foreground uppercase">{q.type}</span>
                  <span className="px-1.5 py-0.5 bg-secondary uppercase">{q.difficulty}</span>
                  <span className="font-mono text-muted-foreground">{q.subject}</span>
                  {q.ai_generated && <Sparkles className="h-3.5 w-3.5 text-primary" />}
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => remove(q.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <p className="mt-2 text-sm font-medium">{q.text}</p>
              {q.options?.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {q.options.map((o, idx) => (
                    <li key={idx} className={`text-sm flex items-center gap-1.5 ${q.correct_answers?.includes(idx) ? "text-emerald-700" : "text-muted-foreground"}`}>
                      {q.correct_answers?.includes(idx) ? <Check className="h-3 w-3" /> : <span className="w-3" />} {o}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
